import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Alert, Button, DatePicker, Spin} from 'antd';
import moment from 'moment';
import {displayAmount} from "../utils/money";
import axios from "axios";
import {authorizationHeaders} from "../utils/api";
import "./DiscountsByReasonReportPage.css";
import {defaultComparer} from "../utils/compare";
import SelectCategoryTree from "../Category/SelectCategoryTree";
import {bindActionCreators} from "redux";
import {getCategoryTree} from "../Category/actions";
import {getCategoryAndChildrenIds} from "../Category/tree";

const maxResults = 10000;

const styles = {
  floatRight: {
    float: 'right',
  },
};

export class DiscountsByReasonReportPage extends Component {
  static propTypes = {
    /* state */
    dispensaryId: PropTypes.string.isRequired,
    categoryTree: PropTypes.object.isRequired,
    /* dispatch */
    getCategoryTree: PropTypes.func.isRequired,
  };

  state = {
    dateTimeFrom: moment().startOf('day'),
    dateTimeTo: moment().endOf('day'),
    loading: false,
    data: null,
    error: '',
    categoryId: '',
  };

  componentDidMount() {
    this._loadCategories();
    this._loadData();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.dispensaryId !== prevProps.dispensaryId) {
      this._loadCategories();
      this._loadData();
    }
    if (this.state.categoryId !== prevState.categoryId) {
      this._loadData();
    }
  }

  _loadCategories() {
    const {dispensaryId, getCategoryTree} = this.props;
    getCategoryTree({dispensaryId});
  }

  _loadData() {
    const filter = {
      where: {
        dispensaryId: {eq: this.props.dispensaryId},
        datetime: {between: [this.state.dateTimeFrom, this.state.dateTimeTo]},
      },
      limit: maxResults,
    };
    if (this.state.categoryId) {
      const categoryIds = getCategoryAndChildrenIds(this.props.categoryTree, this.state.categoryId);
      filter.where.categoryId = {inq: categoryIds};
    }
    this.setState({loading: true, error: ''});
    axios.get(
      `${process.env.PUBLIC_URL}/api/v1/r/discountsByCategoryReport?filter=${encodeURIComponent(JSON.stringify(filter))}&_=${Date.now()}`,
      {headers: authorizationHeaders()}
    ).then(res => this._processData(res.data), err => this.setState({error: err.message}))
      .finally(() => this.setState({loading: false}));
  }

  _processData(data) {
    const {byDiscountReasonId, totalDiscounts} = data.reduce(
      (
        {byDiscountReasonId, totalDiscounts},
        {categoryId, categoryName, discountReasonId, discountReasonName, discount},
      ) => {
        const reason = byDiscountReasonId[discountReasonId] || (byDiscountReasonId[discountReasonId] = {
          discountReasonId, discountReasonName, byCategoryId: {}, totalDiscounts: 0,
        });
        const category = reason.byCategoryId[categoryId] || (reason.byCategoryId[categoryId] = {
          categoryId, categoryName, totalDiscounts: 0,
        });
        reason.totalDiscounts += discount;
        category.totalDiscounts += discount;
        totalDiscounts += discount;
        return {byDiscountReasonId, totalDiscounts};
      },
      {byDiscountReasonId: {}, totalDiscounts: 0}
    );
    const discountReasons = Object.values(byDiscountReasonId)
      .sort(({discountReasonName: a}, {discountReasonName: b}) => defaultComparer(a, b))
      .map(({byCategoryId, ...rest}) => ({
        categories: Object.values(byCategoryId).sort(({categoryName: a}, {categoryName: b}) => defaultComparer(a, b)),
        ...rest
      }));
    this.setState({data: {discountReasons, totalDiscounts, count: data.length}});
  }

  _onCategoryChange = (categoryId) => {
    this.setState({categoryId});
  };

  _onRangePickerChange = (values) => {
    const [dateTimeFrom, dateTimeTo] = values;
    if (!dateTimeFrom || !dateTimeTo) return;
    this.setState({dateTimeFrom, dateTimeTo});
  };

  _print = () => window.print();

  render() {
    const {dateTimeFrom, dateTimeTo, loading, data, categoryId} = this.state;
    const {discountReasons, totalDiscounts} = data || {discountReasons: [], totalDiscounts: 0};
    return (
      <div>
        <div className="transaction-history-report-form">
          <Button icon="printer" style={styles.floatRight} onClick={this._print}>Print</Button>
          <DatePicker.RangePicker
            showTime={{defaultValue: [moment('00:00:00', 'HH:mm:ss'), moment('11:59:59', 'HH:mm:ss')]}}
            format="YYYY-MM-DD HH:mm:ss"
            value={[dateTimeFrom, dateTimeTo]}
            onChange={this._onRangePickerChange}
            onOk={() => this._loadData()}
          />
          <SelectCategoryTree
            tree={this.props.categoryTree}
            showAll
            value={categoryId}
            onChange={this._onCategoryChange}
            style={{minWidth: '300px'}}
          />
          {this._renderAlert()}
        </div>
        <Spin spinning={loading}>
          <h1>Discounts Given</h1>
          <p>
            <strong>Period:</strong> {dateTimeFrom.format('MMMM Do YYYY, h:mm:ss a')} to {dateTimeTo.format('MMMM Do YYYY, h:mm:ss a')}
          </p>
          <table id="discountsByReasonReportTable">
            <thead>
              <tr>
                <th>Discount Reason</th>
                <th>Category</th>
                <th>Total Discount $ Given</th>
              </tr>
            </thead>
            <tbody>
              {discountReasons.map(({discountReasonId, discountReasonName, categories, totalDiscounts}) =>
                <React.Fragment key={discountReasonId}>
                  {categories.map(({categoryId, categoryName, totalDiscounts}, index, {length}) =>
                    <tr key={categoryId}>
                      {index === 0 && <td rowSpan={length+1}>{discountReasonName}</td>}
                      <td>{categoryName}</td>
                      <td>{displayAmount(totalDiscounts)}</td>
                    </tr>
                  )}
                  <tr>
                    <th>Sub-Total</th>
                    <th>{displayAmount(totalDiscounts)}</th>
                  </tr>
                </React.Fragment>
              )}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan={2}>Total</th>
                <th>{displayAmount(totalDiscounts)}</th>
              </tr>
            </tfoot>
          </table>
        </Spin>
      </div>
    );
  }

  _renderAlert() {
    const {error, data} = this.state;
    let type, message, description;
    if (error) {
      type = 'error';
      message = 'Error';
      description = error;
    } else if (!data || data.count === 0) {
      type = 'info';
      message = "No data found";
      description = "Choose a different date range";
    } else if (data.count === maxResults) {
      type = 'warning';
      message = "Too many data";
      description = "Choose a smaller date range";
    } else {
      return null;
    }
    return (<Alert type={type} message={message} description={description}/>);
  }
}

const mapStateToProps = (state) => ({
  dispensaryId: state.dispensary.selectedDispensaryId,
  categoryTree: state.category.categoryTree,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({getCategoryTree}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(DiscountsByReasonReportPage);



// WEBPACK FOOTER //
// ./src/Reports/DiscountsByReasonReportPage.js